import { timestampDate } from '@bufbuild/protobuf/wkt'
import { CopyIcon, UnlockIcon } from 'lucide-react'

import { QuestionnaireStatus } from '@/gen/questionnaire/common/v1/questionnaire_status_pb'

import { useTrackCallback } from '@/lib/analytics/events'
import { relativeTimeFormatted } from '@/lib/date/date'

import { AssessmentDueDatePicker } from '@/pages/assessment/components/assessment-due-date-picker'
import { SendReminderButton } from '@/pages/assessment/questionnaire/send-reminder-button'

import { Button } from '@/components/lemmala-design-system/button/button'
import { Separator } from '@/components/lemmala-design-system/separator/separator'
import { TextWithIcon } from '@/components/lemmala-design-system/text-with-icon/text-with-icon'
import { Tooltip } from '@/components/lemmala-design-system/tooltip/tooltip'
import { SmartEvidenceRequestStatusBadge } from '@/components/shared/smart-evidence-request-status-badge/smart-evidence-request-status-badge'

import { RecipientsButton } from './recipients-button'
import { SmartEvidenceRequestBannerLayout } from './smart-evidence-request-banner-layout'
import type { SmartEvidenceRequestBannerProps } from './types'
import { useSmartEvidenceRequest } from './use-smart-evidence-request.hook'

export const SmartEvidenceRequestOngoingBanner = (props: SmartEvidenceRequestBannerProps) => {
  const { company, smartEvidenceQuestionnaire, readonly, assessmentId, dueDate } = props
  const {
    isUpdating,
    handleCopyLink,
    handlePublish,
    handleUnpublish,
    InviteRecipientModal,
    openInviteRecipientModal,
  } = useSmartEvidenceRequest(props)

  const isSubmitted = smartEvidenceQuestionnaire?.status === QuestionnaireStatus.SUBMITTED
  const companyId = company.id
  const trackDueDateClick = useTrackCallback('third-party.smart-evidence-request.due-date.click')
  const trackDueDateSet = useTrackCallback('third-party.smart-evidence-request.due-date.set')

  const updatedAt = smartEvidenceQuestionnaire?.statusUpdatedAt
    ? timestampDate(smartEvidenceQuestionnaire.statusUpdatedAt)
    : new Date()
  const timeAgo = relativeTimeFormatted(updatedAt)

  return (
    <SmartEvidenceRequestBannerLayout
      title='Smart Evidence Request'
      inviteRecipientModal={!readonly && InviteRecipientModal()}
      description={`shared ${timeAgo}`}
      recipientsButton={
        <div className='flex items-center gap-2'>
          <RecipientsButton
            recipientsEmails={company.recipientsEmails}
            onClick={openInviteRecipientModal}
            readonly={readonly}
          />
          <Separator orientation='vertical' className='h-5' />
          <AssessmentDueDatePicker
            assessmentId={assessmentId}
            dueDate={dueDate}
            readonly={readonly}
            onDatePickerOpen={() => trackDueDateClick({ assessmentId, companyId })}
            onDateSelected={(date) =>
              trackDueDateSet({ assessmentId, companyId, dueDate: date.toISOString() })
            }
          />
          {!readonly && <SendReminderButton company={company} />}
        </div>
      }
      secondaryAction={
        <>
          <SmartEvidenceRequestStatusBadge
            status={smartEvidenceQuestionnaire?.status || QuestionnaireStatus.UNSPECIFIED}
          />

          {!readonly && (
            <Tooltip
              trigger={
                <Button
                  variant='ghost'
                  size='icon-sm'
                  onClick={handleCopyLink}
                  disabled={!smartEvidenceQuestionnaire}
                  className='size-6'
                >
                  <CopyIcon className='size-4' />
                </Button>
              }
            >
              Copy link to smart evidence request
            </Tooltip>
          )}
        </>
      }
      primaryAction={
        <Tooltip
          tooltipDisabled={!readonly}
          trigger={
            <span className='inline-flex'>
              <Button
                variant='outline'
                onClick={isSubmitted ? handlePublish : handleUnpublish}
                disabled={readonly || isUpdating || !smartEvidenceQuestionnaire}
                className='shadow-sm'
              >
                {isSubmitted ? (
                  <TextWithIcon text='Reopen' icon={<UnlockIcon className='size-4' />} />
                ) : (
                  'Unshare'
                )}
              </Button>
            </span>
          }
        >
          {readonly && 'You do not have permission to unpublish'}
        </Tooltip>
      }
    />
  )
}
